import { useCallback, useMemo, useRef, useState } from 'react';
import type { Canceler } from 'axios';
import axios from 'axios';
import { debounce } from 'lodash';

import { useCms } from '~/components/hooks/useCms';
import { reportAxiosError } from '~/Utils';

import type {
  GbgBusinessFindResponse,
  GbgBusinessFindSuggestion,
  GbgBusinessRetrieveResponse,
  GbgBusinessRetrieveResult,
} from './types';

const getBaseRoute = (organizationId: number) => `/api/v1/organizations/${organizationId}/gbg/business`;
interface UseGbgBusinessFindOptions {
  country: string;
  debounceMs?: number;
}

interface UseGbgBusinessFindReturn {
  suggestions: GbgBusinessFindSuggestion[];
  isLoading: boolean;
  search: (input: string) => void;
  clearSuggestions: () => void;
}

export const useGbgBusinessFind = ({
  country,
  debounceMs = 300,
}: UseGbgBusinessFindOptions): UseGbgBusinessFindReturn => {
  const { userOrganization } = useCms();
  const [suggestions, setSuggestions] = useState<GbgBusinessFindSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const cancelRef = useRef<Canceler | null>(null);

  const baseRoute = getBaseRoute(userOrganization.id);

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!input.trim()) {
        setSuggestions([]);
        return;
      }

      cancelRef.current?.();
      let cancelled = false;

      try {
        setIsLoading(true);
        const { data } = await axios.get<GbgBusinessFindResponse>(`${baseRoute}/find`, {
          params: { name: input, country },
          cancelToken: new axios.CancelToken((c) => {
            cancelRef.current = c;
          }),
        });
        setSuggestions(data.suggestions);
      } catch (error) {
        cancelled = axios.isCancel(error);
        if (!cancelled) {
          setSuggestions([]);
          // Don't open a dialog for this error, so the user can continue typing manually
          reportAxiosError(error, false);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    },
    [baseRoute, country]
  );

  const debouncedSearch = useMemo(
    () => debounce((input: string) => fetchSuggestions(input), debounceMs),
    [fetchSuggestions, debounceMs]
  );

  const search = useCallback(
    (input: string) => {
      if (!input.trim()) {
        debouncedSearch.cancel();
        setSuggestions([]);
        return;
      }
      debouncedSearch(input);
    },
    [debouncedSearch]
  );

  const clearSuggestions = useCallback(() => {
    debouncedSearch.cancel();
    cancelRef.current?.();
    setSuggestions([]);
    setIsLoading(false);
  }, [debouncedSearch]);

  return {
    suggestions,
    isLoading,
    search,
    clearSuggestions,
  };
};

interface UseGbgBusinessRetrieveOptions {
  country: string;
}

interface UseGbgBusinessRetrieveReturn {
  retrieve: (name: string) => Promise<GbgBusinessRetrieveResult | null>;
  isLoading: boolean;
}

export const useGbgBusinessRetrieve = ({ country }: UseGbgBusinessRetrieveOptions): UseGbgBusinessRetrieveReturn => {
  const { userOrganization } = useCms();
  const [isLoading, setIsLoading] = useState(false);
  const cancelRef = useRef<Canceler | null>(null);

  const baseRoute = getBaseRoute(userOrganization.id);

  const retrieve = useCallback(
    async (name: string): Promise<GbgBusinessRetrieveResult | null> => {
      if (!name.trim()) {
        return null;
      }

      cancelRef.current?.();

      try {
        setIsLoading(true);
        const { data } = await axios.get<GbgBusinessRetrieveResponse>(`${baseRoute}/retrieve`, {
          params: { name, country },
          cancelToken: new axios.CancelToken((c) => {
            cancelRef.current = c;
          }),
        });
        return data.business;
      } catch (error) {
        if (!axios.isCancel(error)) {
          reportAxiosError(error);
        }
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [baseRoute, country]
  );

  return {
    retrieve,
    isLoading,
  };
};
